import { supabase } from '../lib/supabase';
import { RegisterData, User } from '../types';
import { accountDeletionService } from './accountDeletionService';

interface AuthUser {
  id: string;
  email?: string;
  user_metadata?: Record<string, unknown>;
}

interface SessionLike {
  user?: AuthUser;
}

interface SupabaseResult<T> {
  data: T | null;
  error: Error | null;
}

const normalizeUrl = (url?: string | null): string => {
  if (!url) return '';
  return url.replace(/\/$/, '');
};

const resolveSiteUrl = (): string => {
  const envUrl = normalizeUrl(import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined);
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return normalizeUrl(window.location.origin);
  }
  return '';
};

const buildRedirectUrl = (path: string): string => {
  const baseUrl = resolveSiteUrl();
  if (!baseUrl) {
    throw new Error('Missing site URL for redirect. Set VITE_PUBLIC_SITE_URL.');
  }
  return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
};

const resolveSupabaseStorageKey = (): string | null => {
  const supabaseUrl = normalizeUrl((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL);
  const match = supabaseUrl.match(/^https:\/\/([a-z0-9]+)\.supabase\.co$/i);
  const projectRef = match?.[1];
  return projectRef ? `sb-${projectRef}-auth-token` : null;
};

const getStoredSession = () => {
  try {
    const key = resolveSupabaseStorageKey();
    if (!key) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const user = parsed.user as Record<string, unknown> | undefined;
    if (!user || typeof user.id !== 'string') return null;
    return {
      user: {
        id: user.id as string,
        email: typeof user.email === 'string' ? (user.email as string) : undefined,
        user_metadata: (user.user_metadata as Record<string, unknown>) ?? {},
      },
    };
  } catch {
    return null;
  }
};

export const authService = {
  // OAuth sign‑in with Google
  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: buildRedirectUrl('/auth/callback'),
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) throw error;
    return data;
  },

  // OAuth sign‑in with Apple
  async signInWithApple() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: buildRedirectUrl('/auth/callback'),
      },
    });
    if (error) throw error;
    return data;
  },

  // Sign‑out the current user
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  // Email / password sign‑in
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  // Password‑reset flow – Supabase sends an email, we also send a custom one
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: buildRedirectUrl('/reset-password'),
    });
    if (error) throw error;
    // Log the attempt
    console.log(`Password reset requested for ${email} at ${new Date().toISOString()}`);
  },

  // Send OTP for recovery
  // Note: OTP Expiry time is configured in Supabase Dashboard (Auth > Providers > Email > OTP Expiry).
  // Current policy request: 2 hours (7200 seconds).
  async sendRecoveryOtp(email: string) {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
      }
    });
    if (error) throw error;
    console.log(`Recovery OTP sent to ${email} at ${new Date().toISOString()}`);
  },

  // Verify OTP
  async verifyRecoveryOtp(email: string, token: string) {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    if (error) throw error;
    return data;
  },

  // Update password for authenticated user (used in reset password flow)
  async updatePassword(password: string) {
    const { data, error } = await supabase.auth.updateUser({
      password
    });
    if (error) throw error;
    return data;
  },

  // Registration with age check and welcome email
  async signUp(userData: RegisterData) {
    const birthDate = new Date(userData.birthDate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 18) {
      throw new Error('Debes ser mayor de 18 años para registrarte.');
    }
    const { data, error } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          full_name: userData.fullName,
          username: userData.username,
          birth_date: userData.birthDate,
          user_type: userData.userType,
          didit_session_id: userData.diditSessionId,
          didit_status: userData.diditStatus
        }
      }
    });
    if (error) throw error;

    // If we have a user and session, try to update the profile directly to ensure verification status is synced
    if (data.user && data.session && userData.diditSessionId) {
       try {
         const verificationStatus = userData.diditStatus === 'approved' ? 'verified' : 
                                    (userData.diditStatus === 'declined' ? 'rejected' : 'pending');
         
         await supabase.from('profiles').update({
            age_verification_status: verificationStatus,
            age_verified: verificationStatus === 'verified'
         }).eq('id', data.user.id);
       } catch (e) {
         console.error('Error syncing initial profile status:', e);
       }
    }

    // await sendMail(userData.email, '¡Bienvenido a SoArtistic!', 'Gracias por registrarte. ¡Explora y comparte tu arte!');
    console.log('Welcome email requested (custom email disabled in browser environment)');
    return data;
  },

  // Retrieve the current authenticated user together with their profile
  async getCurrentUser() {
    try {
      const sessionResult = await Promise.race([
        supabase.auth.getSession(),
        new Promise<{ data: { session: null }; error: Error }>((resolve) =>
          setTimeout(() => resolve({ data: { session: null }, error: new Error('getSession timeout') }), 2000)
        ),
      ]);

      const session = (sessionResult?.data?.session as SessionLike | undefined) ?? getStoredSession() as SessionLike | null;
      const user = session?.user;
      if (!user) return null;

      try {
        const profileResult = await Promise.race([
          supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .maybeSingle(),
          new Promise<SupabaseResult<Record<string, unknown>>>((resolve) =>
            setTimeout(() => resolve({ data: null, error: new Error('profile timeout') as Error }), 3000)
          ),
        ]);

        const profile = (profileResult as SupabaseResult<Record<string, unknown>>)?.data;
        const error = (profileResult as SupabaseResult<Record<string, unknown>>)?.error;

        if (error) {
          console.error('Error fetching profile:', error);
          return {
            id: user.id,
            email: user.email,
            username: user.email?.split('@')[0] || 'user',
            full_name: user.user_metadata?.full_name || 'User',
            user_type: 'visitor',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        }
        return profile ? { id: user.id, email: user.email, ...profile } : null;
      } catch (profileError) {
        console.error('Exception fetching profile:', profileError);
        return {
          id: user.id,
          email: user.email,
          username: user.email?.split('@')[0] || 'user',
          full_name: user.user_metadata?.full_name || 'User',
          user_type: 'visitor',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
    } catch (err) {
      console.warn('getCurrentUser error or timeout:', err);
      return null;
    }
  },

  onAuthStateChange: (callback: (user: User | null) => void) => {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state change:', event, session ? { user_id: session.user?.id } : null);

      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESH_FAILED') {
        callback(null);
        return;
      }

      if (session?.user) {
        const user = await authService.getCurrentUser();
        console.log('🔍 Current user after change:', user ? { id: user.id, username: user.username } : null);
        if (user) {
          callback(user as User);
        } else {
          console.warn('Auth state change received but getCurrentUser returned null; preserving current user state to avoid unintended logout.');
        }
      } else if (event === 'INITIAL_SESSION') {
        callback(null);
      }
    });
  },

  async createSocialProfile(user: AuthUser) {
    const fullName =
      user.user_metadata?.full_name ||
      user.user_metadata?.name ||
      `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() ||
      user.email?.split('@')[0] ||
      'Usuario';

    const baseUsername = user.email?.split('@')[0] || fullName.toLowerCase().replace(/\s+/g, '_') || 'usuario';
    let username = baseUsername;
    let counter = 1;
    while (true) {
      const { data: existing } = await supabase.from('profiles').select('id').eq('username', username).single();
      if (!existing) break;
      username = `${baseUsername}_${counter}`;
      counter++;
    }

    const { error: profileError } = await supabase.from('profiles').insert({
      id: user.id,
      username,
      full_name: fullName,
      birth_date: '1990-01-01',
      user_type: 'visitor',
      avatar_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || null,
      account_status: 'active',
    });
    if (profileError) {
      console.error('Error creating profile:', profileError);
    }

    // Wait for the profile to become visible (trigger may run asynchronously)
    let retries = 0;
    const maxRetries = 10;
    while (retries < maxRetries) {
      try {
        const { data: profile, error } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
        if (error && error.code !== 'PGRST116') throw error;
        if (profile) {
          return { id: user.id, email: user.email!, ...profile };
        }
        if (retries < maxRetries - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          retries++;
        } else {
          console.warn('Profile not found after retries, returning fallback');
          return {
            id: user.id,
            email: user.email!,
            username,
            full_name: fullName,
            birth_date: '1990-01-01',
            user_type: 'visitor' as const,
            is_premium: false,
            avatar_url: user.user_metadata?.avatar_url || null,
            account_status: 'active',
          };
        }
      } catch (err) {
        console.error('Error fetching profile after creation:', err);
        return null;
      }
    }
    return null;
  },

  async deleteAccount() {
    return accountDeletionService.requestAccountDeletion();
  }
};
